import { Box, Typography, Skeleton } from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';
import TimelineDot from '@mui/lab/TimelineDot';
import {
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  Timeline,
} from '@mui/lab';
import { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import useLiveData from '@/hooks/useLiveData';
import useApiQuery from '@/hooks/useApiQuery';
import { useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

const formatDate = (date) => {
  if (!date) return 'Kein Datum';
  const d = new Date(date);
  if (isNaN(d)) return 'Kein Datum';
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${d.getFullYear()} ${d
    .getHours()
    .toString()
    .padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d
    .getSeconds()
    .toString()
    .padStart(2, '0')} Uhr`;
};

export default function ToolHistory({ data: tool }) {
  const { users } = useRouteLoaderData('auth');
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: history, isPending: isPendingHistory } = useApiQuery(
    'history',
    {
      $and: [
        { collection: 'tools' },
        { operationType: 'update' },
        { documentId: tool._id },
      ],
    },
    true,
  );

  useLiveData('history');

  const historyEntries = useMemo(() => {
    const entries = history?.filter((h) => h.documentId === tool._id) || [];

    const getDate = (value) => {
      if (!value) return null;
      const d = new Date(value);
      return isNaN(d) ? null : d;
    };

    return entries
      .map((entry) => {
        const userId =
          entry.updatedFields?.takenBy ||
          entry.user?._id ||
          entry.updatedFields?.changedBy ||
          entry.user;
        const owner = users.find((u) => u._id === userId);

        const fields = entry.updateDescription?.updatedFields || {};
        const comment = fields.content;

        let statusText = 'Geändert';
        let dateRaw = getDate(entry.changedAt || entry.updatedAt);

        // Status geändert
        if (typeof fields.status === 'string') {
          switch (fields.status.toUpperCase()) {
            case 'TAKEN':
              statusText = 'Mitgenommen';
              break;
            case 'AVAILABLE':
              statusText = 'Zurückgelegt';
              break;
          }
        }

        // toolStatus geändert
        if (typeof fields.toolStatus === 'string') {
          switch (fields.toolStatus.toUpperCase()) {
            case 'BROKEN':
              statusText = 'Defekt';
              break;
            case 'MISSING':
              statusText = 'Fehlt';
              break;
          }
        }

        const description = comment
          ? `Status: ${statusText}
          \nKommentar: ${comment}`
          : `Status: ${statusText}`;

        return {
          date: formatDate(dateRaw),
          dateRaw,
          user: owner
            ? `${owner.firstName} ${owner.lastName}`
            : 'Unbekannter Nutzer',
          title: 'Historie',
          statusText,
          comment,
          description,
        };
      })
      .sort((a, b) => b.dateRaw - a.dateRaw);
  }, [history, users]);
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 1,
        overflowY: 'auto',
        paddingRight: 1,
        height: '100%',
      }}
    >
      {isPendingHistory ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: 1,
          }}
        >
          {[...Array(5)].map((_, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 3,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  flex: 'unset',
                  ml: sm ? 2 : 6,
                  width: sm ? '30%' : '13%',
                }}
              >
                <Skeleton width="100%" height={sm ? 80 : 60} />
                <Skeleton width="100%" height={sm ? 80 : 60} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  flex: 'unset',
                  width: sm ? '40%' : '30%',
                }}
              >
                <Skeleton width="80%" height={sm ? 80 : 60} />
                <Skeleton width="80%" height={sm ? 80 : 60} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.2 },
          }}
        >
          {historyEntries.map((item, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent color="textSecondary">
                <Typography variant="body2">{item.date}</Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot />
                {index < historyEntries.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="body2">{item.user}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Status: {item.statusText}
                </Typography>
                {item.comment && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 0.5 }}
                  >
                    Kommentar: {item.comment}
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
}
